/**
 * Formatting Utilities
 * Functions for formatting dates, currency, file sizes, etc.
 */

import { CURRENCY, WORKFLOW_STATUS_LABELS, TASK_STATUS_LABELS, TASK_TYPE_LABELS } from './constants';

/**
 * Format date to human-readable format
 * @param {string|Date} date - Date to format
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} Formatted date string
 */
export function formatDate(date, includeTime = false) {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return 'Invalid Date';

  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }

  return dateObj.toLocaleDateString('en-MY', options);
}

/**
 * Format date to relative time (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export function formatRelativeTime(date) {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now - dateObj;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  if (diffDay < 30) {
    const weeks = Math.floor(diffDay / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDay < 365) {
    const months = Math.floor(diffDay / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDay / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @param {boolean} includeSymbol - Whether to include currency symbol
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, includeSymbol = true) {
  if (amount === null || amount === undefined || isNaN(amount)) return 'N/A';

  const formatted = parseFloat(amount).toLocaleString(CURRENCY.LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return includeSymbol ? `${CURRENCY.SYMBOL} ${formatted}` : formatted;
}

/**
 * Format file size to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Format phone number in Malaysian format: +60 XX-XXXX XXXX
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(phone) {
  if (!phone) return 'N/A';

  // Remove all non-digit characters
  let digits = phone.replace(/\D/g, '');

  // Remove leading 60 if present (country code)
  if (digits.startsWith('60')) {
    digits = digits.substring(2);
  }

  // Remove leading 0 if present
  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }

  // Format based on number type
  if (digits.length >= 9) {
    // Mobile numbers
    if (digits[0] === '1') {
      const prefix = digits.substring(0, 2);  // 1X

      // Special case: 11 prefix format is 11-XXXX XXXX (4-4 pattern)
      if (prefix === '11' && digits.length >= 10) {
        const middle = digits.substring(2, 6);   // XXXX (4 digits)
        const last = digits.substring(6, 10);    // XXXX (4 digits)
        return `+60 ${prefix}-${middle} ${last}`;
      } else {
        // Other mobile numbers: 1X-XXX XXXX (3-4 pattern)
        const middle = digits.substring(2, 5);   // XXX (3 digits)
        const last = digits.substring(5, 9);     // XXXX (4 digits)
        return `+60 ${prefix}-${middle} ${last}`;
      }
    } else {
      // Landline: +60 X-XXXX XXXX
      const areaCode = digits.substring(0, 1);  // X
      const middle = digits.substring(1, 5);     // XXXX
      const last = digits.substring(5, 9);       // XXXX
      return `+60 ${areaCode}-${middle} ${last}`;
    }
  }

  // Return with +60 prefix for incomplete numbers
  return `+60 ${digits}`;
}

/**
 * Format project number
 * @param {string} projectNumber - Project number to format
 * @returns {string} Formatted project number
 */
export function formatProjectNumber(projectNumber) {
  if (!projectNumber) return 'N/A';
  return projectNumber.toUpperCase();
}

/**
 * Format workflow status to human-readable label
 * @param {string} status - Workflow status
 * @returns {string} Human-readable status label
 */
export function formatWorkflowStatus(status) {
  return WORKFLOW_STATUS_LABELS[status] || status;
}

/**
 * Format task status to human-readable label
 * @param {string} status - Task status
 * @returns {string} Human-readable status label
 */
export function formatTaskStatus(status) {
  return TASK_STATUS_LABELS[status] || status;
}

/**
 * Format task type to human-readable label
 * @param {string} type - Task type
 * @returns {string} Human-readable type label
 */
export function formatTaskType(type) {
  return TASK_TYPE_LABELS[type] || type;
}

/**
 * Format percentage
 * @param {number} value - Value to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage
 */
export function formatPercentage(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `${parseFloat(value).toFixed(decimals)}%`;
}

/**
 * Compute period-over-period delta between two values.
 * @param {number} current - Current-period value
 * @param {number} previous - Prior-period value
 * @returns {{text: string, direction: 'up'|'down'|'flat', pct: number|null}}
 */
export function formatDelta(current, previous) {
  if (
    current === null || current === undefined || isNaN(current) ||
    previous === null || previous === undefined || isNaN(previous) ||
    previous === 0
  ) {
    return { text: '—', direction: 'flat', pct: null };
  }
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(pct) < 0.5) return { text: '— 0%', direction: 'flat', pct: 0 };
  const direction = pct > 0 ? 'up' : 'down';
  const arrow = direction === 'up' ? '▲' : '▼';
  return { text: `${arrow} ${Math.abs(pct).toFixed(0)}%`, direction, pct };
}

/**
 * Format user name
 * @param {object} user - User object with name or firstName/lastName
 * @returns {string} Formatted user name
 */
export function formatUserName(user) {
  if (!user) return 'N/A';

  if (user.name) return user.name;
  if (user.fullName) return user.fullName;
  if (user.nickname) return user.nickname;
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  if (user.firstName) return user.firstName;

  return 'Unknown User';
}

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export function truncateText(text, maxLength = 50) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Format deadline with urgency indicator
 * @param {string|Date} deadline - Deadline date
 * @returns {object} Object with formatted deadline and urgency level
 */
export function formatDeadline(deadline) {
  if (!deadline) return { text: 'No deadline', urgency: 'none' };

  const deadlineDate = typeof deadline === 'string' ? new Date(deadline) : deadline;
  const now = new Date();
  const diffMs = deadlineDate - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  let urgency = 'low';
  let text = formatDate(deadline);

  if (diffDays < 0) {
    urgency = 'overdue';
    text = `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''}`;
  } else if (diffDays === 0) {
    urgency = 'critical';
    text = 'Due today';
  } else if (diffDays === 1) {
    urgency = 'high';
    text = 'Due tomorrow';
  } else if (diffDays <= 3) {
    urgency = 'high';
    text = `Due in ${diffDays} days`;
  } else if (diffDays <= 7) {
    urgency = 'medium';
    text = `Due in ${diffDays} days`;
  } else {
    urgency = 'low';
    text = `Due on ${formatDate(deadline)}`;
  }

  return { text, urgency, daysRemaining: diffDays };
}

/**
 * Format address (multi-line)
 * @param {object} address - Address object
 * @returns {string} Formatted address
 */
export function formatAddress(address) {
  if (!address) return 'N/A';

  if (typeof address === 'string') return address;

  const parts = [
    address.street,
    address.city,
    address.state,
    address.postalCode,
    address.country
  ].filter(Boolean);

  return parts.join(', ');
}

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials (max 2 characters)
 */
export function getInitials(name) {
  if (!name) return '?';

  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Format duration (milliseconds to human-readable)
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Formatted duration
 */
export function formatDuration(ms) {
  if (!ms) return '0s';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

/**
 * Format number with abbreviation (e.g., 1.5K, 2.3M)
 * @param {number} num - Number to format
 * @returns {string} Abbreviated number
 */
export function formatNumberAbbreviation(num) {
  if (num === null || num === undefined) return 'N/A';

  if (num < 1000) return num.toString();
  if (num < 1000000) return (num / 1000).toFixed(1) + 'K';
  if (num < 1000000000) return (num / 1000000).toFixed(1) + 'M';
  return (num / 1000000000).toFixed(1) + 'B';
}

/**
 * Convert a string to Title Case (every word's first letter capitalized, the rest lowercased).
 * Handles hyphens and apostrophes (e.g. "o'brien" → "O'Brien").
 * @param {string} str - Input string
 * @returns {string} Title-cased string (or the original value if falsy/non-string)
 */
export function toTitleCase(str) {
  if (!str || typeof str !== 'string') return str;
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format property type to human-readable label
 * @param {string} propertyType - Property type code
 * @returns {string} Human-readable property type
 */
export function formatPropertyType(propertyType) {
  if (!propertyType) return 'N/A';

  const propertyTypeLabels = {
    'apartment': 'Apartment/Flat',
    'condo': 'Condominium',
    'landed': 'Landed House',
    'landed_single': 'Landed - Single Storey',
    'landed_double': 'Landed - Double Storey',
    'landed_triple': 'Landed - Triple Storey',
    'terrace_single': 'Terrace House - Single Storey',
    'terrace_double': 'Terrace House - Double Storey',
    'terrace_triple': 'Terrace House - Triple Storey',
    'semi_d_single': 'Semi-Detached - Single Storey',
    'semi_d_double': 'Semi-Detached - Double Storey',
    'semi_d_triple': 'Semi-Detached - Triple Storey',
    'bungalow': 'Bungalow',
    'townhouse': 'Townhouse',
    'hdb': 'HDB',
    'commercial': 'Commercial',
    'other': 'Other'
  };

  return propertyTypeLabels[propertyType] || propertyType;
}

export default {
  formatDate,
  formatRelativeTime,
  formatCurrency,
  formatFileSize,
  formatPhoneNumber,
  formatProjectNumber,
  formatWorkflowStatus,
  formatTaskStatus,
  formatTaskType,
  formatPercentage,
  formatDelta,
  formatUserName,
  truncateText,
  formatDeadline,
  formatAddress,
  getInitials,
  formatDuration,
  formatNumberAbbreviation,
  formatPropertyType,
  toTitleCase
};
